/**
 * RelayMD's "Sign in with MedicAI" opens `/relaymd/sign-in?challenge=…&state=…` in the phone's browser. The challenge
 * is the SHA-256 of a secret the app keeps (base64url, 43 characters); the state is the app's own value, handed back
 * unchanged. The token always goes to the app's fixed address, never to one named in the request.
 */
export const RELAYMD_REDIRECT = 'relaymd://auth/medicai';
export const RELAYMD_SIGN_IN_PATH = '/relaymd/sign-in';

export interface RelaymdRequest {
  challenge: string;
  state: string;
}

export function readRelaymdRequest(query: URLSearchParams): RelaymdRequest | null {
  const challenge = query.get('challenge') ?? '';
  const state = query.get('state') ?? '';
  if (!/^[A-Za-z0-9_-]{43}$/.test(challenge) || !/^[A-Za-z0-9_-]{16,200}$/.test(state)) return null;
  return { challenge, state };
}

export const relaymdReturnUrl = (token: string, state: string): string =>
  `${RELAYMD_REDIRECT}#token=${encodeURIComponent(token)}&state=${encodeURIComponent(state)}`;

export interface RelaymdAccount {
  name: string;
  /** shown under the name; null when the name already is the email */
  email: string | null;
  initials: string;
}

/** Who RelayMD will sign in as, from the MedicAI profile: "Dr Ann Lee", with a title typed in capitals put in plain case. */
export function relaymdAccount(user: { title?: string; name?: string; surname?: string; email?: string } | null | undefined): RelaymdAccount | null {
  if (!user) return null;
  const title = user.title ? user.title.charAt(0).toUpperCase() + user.title.slice(1).toLowerCase() : '';
  const person = [user.name, user.surname].filter(Boolean).join(' ');
  if (!person) {
    const email = user.email ?? '';
    return email ? { name: email, email: null, initials: email.charAt(0).toUpperCase() } : null;
  }
  const initials = [user.name, user.surname].filter(Boolean).map((part) => part!.charAt(0).toUpperCase()).join('');
  return { name: [title, person].filter(Boolean).join(' '), email: user.email ?? null, initials };
}
